import type { CryptoApi } from 'matrix-js-sdk/lib/crypto-api'
import type { VerificationRequest } from 'matrix-js-sdk/lib/crypto-api'
import { requireClient, cacheRecoveryKey } from './client'

export interface OwnSession {
  deviceId: string
  displayName: string
  isCurrent: boolean
  verified: boolean
  dehydrated: boolean
}

export function getCrypto(): CryptoApi {
  const crypto = requireClient().getCrypto()
  if (!crypto) throw new Error('Encryption is not available')
  return crypto
}

/** List this account's sessions (devices) with their verification status. */
export async function listOwnSessions(): Promise<OwnSession[]> {
  const client = requireClient()
  const crypto = getCrypto()
  const me = client.getUserId()
  if (!me) return []

  const deviceMap = await crypto.getUserDeviceInfo([me])
  const devices = deviceMap.get(me) ?? new Map()
  const currentId = client.getDeviceId()

  const sessions: OwnSession[] = []
  for (const [deviceId, device] of devices) {
    const status = await crypto.getDeviceVerificationStatus(me, deviceId)
    sessions.push({
      deviceId,
      displayName: device.displayName || deviceId,
      isCurrent: deviceId === currentId,
      verified: status?.isVerified() ?? false,
      dehydrated: device.dehydrated,
    })
  }

  // Current device first, then unverified, then by name.
  return sessions.sort((a, b) => {
    if (a.isCurrent !== b.isCurrent) return a.isCurrent ? -1 : 1
    if (a.verified !== b.verified) return a.verified ? 1 : -1
    return a.displayName.localeCompare(b.displayName)
  })
}

/** Whether cross-signing is set up and usable on this device. */
export async function isCrossSigningReady(): Promise<boolean> {
  return getCrypto().isCrossSigningReady()
}

export type CrossSigningState =
  | 'ready' // set up and usable on this device
  | 'locked' // exists on the account, but not loaded on this device yet
  | 'not_set_up' // no cross-signing keys exist for the account

/** Distinguish "ready" vs "exists but locked here" vs "never set up". */
export async function getCrossSigningState(): Promise<CrossSigningState> {
  const crypto = getCrypto()
  if (await crypto.isCrossSigningReady()) return 'ready'
  const status = await crypto.getCrossSigningStatus()
  if (status.privateKeysInSecretStorage || status.publicKeysOnDevice) return 'locked'
  return 'not_set_up'
}

/**
 * Load existing cross-signing keys onto this device using the recovery key.
 * Used when cross-signing exists on the account but isn't available here yet.
 */
export async function unlockCrossSigning(recoveryKey: string): Promise<void> {
  cacheRecoveryKey(recoveryKey) // satisfies the getSecretStorageKey crypto callback
  await getCrypto().bootstrapCrossSigning({})
}

/**
 * Create cross-signing keys for an account that has none. Uploading the new
 * device-signing keys requires re-authentication (password).
 */
export async function setUpCrossSigning(password: string): Promise<void> {
  const client = requireClient()
  const userId = client.getUserId()
  if (!userId) throw new Error('Not logged in')
  await getCrypto().bootstrapCrossSigning({
    setupNewCrossSigning: true,
    authUploadDeviceSigningKeys: async (makeRequest) => {
      await makeRequest({
        type: 'm.login.password',
        identifier: { type: 'm.id.user', user: userId },
        password,
      })
    },
  })
}

/** Begin verifying another of your own sessions (emoji / SAS). */
export async function requestSelfVerification(deviceId: string): Promise<VerificationRequest> {
  const client = requireClient()
  return getCrypto().requestDeviceVerification(client.getUserId()!, deviceId)
}
